import { cleanS3Url, MAX_CP_NAME_LENGTH, truthy } from '@bynder-studio/misc';
import { v4 as uuidv4 } from 'uuid';
import { FontFamily } from '@bynder-studio/structured-text';
import type { ContentProperty, ElementForContentProperty, Reason, SanitizedContentProperty } from '../types';
import { ImageElement } from '../Models/Elements/ImageElement';
import { VideoElement } from '../Models/Elements/VideoElement';
import { TextElement } from '../Models/Elements/TextElement';
import { ShapeElement } from '../Models/Elements/ShapeElement';
import { ElementTypes } from '../Enums/ElementTypes';
import { deepClone, equals } from './utils';
import { DynamicEventEmitter } from './DynamicEventEmitter';
import { ElementUpdatedEventData } from '../event-types';
import { getElementType } from './elementUtils';
import { ElementUpdateTypes } from '../Enums/ElementUpdateTypes';
import type { BaseMultiPageModel } from '../Models/Models/BaseMultiPageModel';
import { ShapeTypes } from '../Enums/ShapeTypes';
import type { BaseVisualElement } from '../Models/Elements/BaseVisualElement';
import { ContentPropertyData } from '../SpecificationParser/types';
import { BaseModel } from '../Models/Models/BaseModel';
import { exportContentProperty } from '../SpecificationParser/SpecificationExporter';
import { WebSpecificationParser } from '../SpecificationParser/WebSpecificationParser';
import { AudioElement } from '../Models/Elements/AudioElement';

export class ContentPropertiesManager {
    // Filter provided properties and return only accociated with CP
    static getSyncedPropertiesList(element: ElementForContentProperty, properties: string[]): string[] {
        // For now filter only audio element because only this element has
        // changeable properties in the CC that are not shared between CPs
        if (element instanceof AudioElement) {
            return ['src', 'srcId', 'srcType', 'fileName', 'offsetTime', 'virtualData'];
        }

        return properties;
    }

    private static createPropertiesFromElement(element: ElementForContentProperty): ContentProperty['properties'] {
        if (element instanceof ImageElement) {
            return {
                src: element.src,
                srcId: element.srcId,
                srcType: element.srcType,
                fileName: element.fileName,
                naturalDimension: {
                    width: element.naturalDimension.width,
                    height: element.naturalDimension.height,
                },
                virtualData: { ...element.virtualData },
            };
        }

        if (element instanceof VideoElement) {
            return {
                src: element.src,
                srcId: element.srcId,
                srcType: element.srcType,
                fileName: element.fileName,
                offsetTime: element.offsetTime,
                isAlpha: element.isAlpha,
                useAudio: element.useAudio,
                useDynamicLength: element.useDynamicLength,
                naturalDimension: {
                    width: element.naturalDimension.width,
                    height: element.naturalDimension.height,
                },
                virtualData: { ...element.virtualData },
                gain: element.gain,
                fadeIn: element.fadeIn,
                fadeOut: element.fadeOut,
            };
        }

        if (element instanceof AudioElement) {
            return {
                src: element.src,
                srcId: element.srcId,
                srcType: element.srcType,
                fileName: element.fileName,
                offsetTime: element.offsetTime,
                virtualData: { ...element.virtualData },
            };
        }

        if (element instanceof TextElement) {
            const runs = deepClone(element.formattedText.runs);
            const hasNonEmpty = runs.some((run) => run.length > 0);

            return {
                formattedText: {
                    value: element.formattedText.value,
                    layoutRuns: element.formattedText.layoutRuns,
                    runs: hasNonEmpty ? runs.filter((run) => run.length > 0) : [runs[0]],
                },
                limitTextToBounds: element.limitTextToBounds,
                textStyles: [...element.textStyles],
                brandColors: [...element.brandColors],
            };
        }

        if (element instanceof ShapeElement) {
            return {
                shapeType: element.shapeType,
                path: element.shapeType === ShapeTypes.CUSTOM ? element.path : '',
                fillColor: element.fillColor,
                borderColor: element.borderColor,
                borderWidth: element.borderWidth,
                borderAlignment: element.borderAlignment,
                borderLineCap: element.borderLineCap,
                borderRadius: element.borderRadius,
                fillBrandColors: element.fillBrandColors,
                borderBrandColors: element.borderBrandColors,
            };
        }

        return null;
    }

    static sanitizeContentProperty(contentProperty: SanitizedContentProperty): SanitizedContentProperty {
        switch (contentProperty.type) {
            case ElementTypes.IMAGE:
            case ElementTypes.VIDEO:
            case ElementTypes.AUDIO: {
                if ('src' in contentProperty.properties) {
                    return {
                        ...contentProperty,
                        properties: {
                            ...contentProperty.properties,
                            src: cleanS3Url(contentProperty.properties.src),
                        },
                    } as SanitizedContentProperty;
                }

                return contentProperty;
            }

            case ElementTypes.TEXT: {
                if ('textStyles' in contentProperty.properties && 'brandColors' in contentProperty.properties) {
                    const { textStyles, brandColors, ...props } = contentProperty.properties;

                    return {
                        ...contentProperty,
                        properties: {
                            ...props,
                        },
                    } as SanitizedContentProperty;
                }

                return contentProperty;
            }

            default: {
                return contentProperty;
            }
        }
    }

    private contentProperties: ContentProperty[] = [];

    private eventEmitter: DynamicEventEmitter;

    private multiPageModel: BaseMultiPageModel;

    handleElementUpdated = (params: ElementUpdatedEventData & { reason: Reason }) => {
        const { element, updateTypes, reason } = params;

        if (!element.contentPropertyId) {
            return;
        }

        const contentProperty = this.getContentProperty(element.contentPropertyId);

        if (!contentProperty) {
            return;
        }

        const newContentProperty: ContentProperty = { ...contentProperty };
        newContentProperty.properties = {
            ...ContentPropertiesManager.createPropertiesFromElement(element as ElementForContentProperty),
        };

        if (
            equals(
                ContentPropertiesManager.sanitizeContentProperty(newContentProperty),
                ContentPropertiesManager.sanitizeContentProperty(contentProperty),
            )
        ) {
            return;
        }

        if (updateTypes.has(ElementUpdateTypes.CONTENT_PROPERTY)) {
            return;
        }

        this.updateContentProperty(newContentProperty, reason);

        this.multiPageModel.getModels().forEach((model) => {
            model.getAllElementsRecursively().forEach((el) => {
                if ((el as BaseVisualElement).contentPropertyId === newContentProperty.uuid && el.id !== element.id) {
                    model.updateElement(el.id, deepClone(newContentProperty.properties), { reason });
                }
            });
        });
    };

    getContentPropertiesByType(type: ContentProperty['type']) {
        return this.contentProperties.filter((value) => value.type === type);
    }

    get allContentProperties() {
        return this.contentProperties;
    }

    getContentProperty(uuid: string) {
        const contentProperty = this.contentProperties.find((s) => s.uuid === uuid);

        if (!contentProperty) {
            return null;
        }

        return deepClone(contentProperty);
    }

    setMultiPageModel<T extends BaseModel>(multiPageModel: BaseMultiPageModel<T>) {
        this.multiPageModel = multiPageModel;
    }

    setEventEmitter(eventEmitter: DynamicEventEmitter) {
        this.unsubscribe();
        this.eventEmitter = eventEmitter;
        this.eventEmitter.on('elementUpdated', this.handleElementUpdated);
    }

    unsubscribe() {
        if (!this.eventEmitter) {
            return;
        }

        this.eventEmitter.off('elementUpdated', this.handleElementUpdated);
    }

    loadContentProperties(contentProperties: ContentProperty[]) {
        this.contentProperties = contentProperties;
    }

    extendContentProperties(
        contentProperties: ContentPropertyData[],
        fontFamilies: FontFamily[],
        updatedStyleIds: Map<string, string>,
        forceExtend = false,
    ) {
        const updatedIds = new Map<ContentProperty['uuid'], ContentProperty['uuid']>();

        if (contentProperties.length === 0) {
            return updatedIds;
        }

        const newContentProperties = WebSpecificationParser.parseContentProperties(
            contentProperties,
            fontFamilies,
        ).filter((contentProperty: ContentProperty) => {
            const { uuid, ...propRaw } = contentProperty;

            return !this.contentProperties.some(({ uuid: oldUuid, ...existingPropRaw }) => {
                const propCleaned = ContentPropertiesManager.sanitizeContentProperty(propRaw);
                const existingPropCleaned = ContentPropertiesManager.sanitizeContentProperty(existingPropRaw);

                if (propCleaned.type === ElementTypes.TEXT && 'formattedText' in propCleaned.properties) {
                    propCleaned.properties.formattedText.runs.forEach((run) => {
                        if (run.styleId && updatedStyleIds.has(run.styleId)) {
                            run.styleId = updatedStyleIds.get(run.styleId);
                        }
                    });
                }

                if (forceExtend) {
                    return false;
                }

                const result = equals(existingPropCleaned, propCleaned);

                if (result) {
                    updatedIds.set(uuid, oldUuid);
                }

                return result;
            });
        });

        newContentProperties.forEach((contentProperty) => {
            const uuid = uuidv4();
            updatedIds.set(contentProperty.uuid, uuid);
            contentProperty.uuid = uuid;
        });

        this.contentProperties = [...this.contentProperties, ...newContentProperties];

        return updatedIds;
    }

    adjustSimilarContentPropertiesNames(contentProperties: ContentPropertyData[]) {
        const generateNewName = (oldName: string, names: Set<string>) => {
            if (!names.size) {
                return oldName;
            }

            let suffix = 1;
            let newName = `${oldName}.${suffix}`;

            while (names.has(newName)) {
                suffix++;
                newName = `${oldName}.${suffix}`;
            }

            return newName;
        };

        contentProperties.forEach((contentProperty) => {
            const re = new RegExp(`^${contentProperty.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?:\\.\\d+)?$`);
            const existingNames = [
                ...this.contentProperties,
                ...contentProperties.filter(
                    (parsedContentProperty) => parsedContentProperty.uuid !== contentProperty.uuid,
                ),
            ]
                .filter((existingContentProperty) => {
                    if (existingContentProperty.type !== contentProperty.type) {
                        return false;
                    }

                    return re.test(existingContentProperty.name);
                })
                .map((existingContentProperty) => existingContentProperty.name);

            contentProperty.name = generateNewName(contentProperty.name, new Set(existingNames));
        });
    }

    createContentPropertyFromElement(element: ElementForContentProperty, name: string) {
        const validatedName = name.slice(0, MAX_CP_NAME_LENGTH);
        const type = getElementType(element) as
            | ElementTypes.IMAGE
            | ElementTypes.VIDEO
            | ElementTypes.TEXT
            | ElementTypes.SHAPE
            | ElementTypes.AUDIO;

        const commonProperties = {
            uuid: uuidv4(),
            name: validatedName,
            type,
            properties: ContentPropertiesManager.createPropertiesFromElement(element),
        };

        if (!commonProperties.properties) {
            return null;
        }

        return this.createContentProperty(commonProperties);
    }

    updateContentProperty(contentProperty: Partial<ContentProperty>, reason: Reason = 'user') {
        const index = this.contentProperties.findIndex((s) => s.uuid === contentProperty.uuid);

        if (index === -1) {
            return;
        }

        const oldValue: Partial<ContentProperty> = {};
        Object.entries(contentProperty).forEach(([key, value]) => {
            oldValue[key] = this.contentProperties[index][key];
            this.contentProperties[index][key] = value;
        });

        this.eventEmitter.emit('contentPropertyChanged', {
            action: 'update',
            oldValue,
            newValue: contentProperty,
            reason,
        });

        if (!this.multiPageModel) {
            return;
        }

        this.multiPageModel.getModels().forEach((model, index) => {
            model.getAllElementsRecursively().forEach((el) => {
                if ((el as BaseVisualElement).contentPropertyId === contentProperty.uuid) {
                    model.updateElement(el.id, deepClone(contentProperty.properties), { reason });
                }
            });
        });
    }

    updateContentProperties(contentProperties: ContentProperty[]) {
        const deletedPropsIds = new Set(this.contentProperties.map((contentProperty) => contentProperty.uuid));
        const updatedProps = contentProperties
            .map((newContentProperty) => {
                const oldContentProperty = this.contentProperties.find(
                    (contentProperty) => contentProperty.uuid === newContentProperty.uuid,
                );

                if (!oldContentProperty) {
                    return null;
                }

                if (oldContentProperty.name !== newContentProperty.name) {
                    this.updateContentProperty({ uuid: newContentProperty.uuid, name: newContentProperty.name });
                }

                deletedPropsIds.delete(oldContentProperty.uuid);

                return oldContentProperty;
            })
            .filter(truthy);
        deletedPropsIds.forEach((uuid) => this.deleteContentProperty(uuid));
        this.contentProperties = updatedProps;
    }

    deleteContentProperty(uuid: string, reason: Reason = 'user') {
        const index = this.contentProperties.findIndex((s) => s.uuid === uuid);

        if (index === -1) {
            return;
        }

        const [contentProperty] = this.contentProperties.splice(index, 1);
        this.eventEmitter.emit('contentPropertyChanged', {
            action: 'remove',
            oldValue: contentProperty,
            newValue: null,
            reason,
        });

        if (!this.multiPageModel) {
            return;
        }

        this.multiPageModel.getModels().forEach((model) => {
            model.getAllElementsRecursively().forEach((el) => {
                if ((el as BaseVisualElement).contentPropertyId === contentProperty.uuid) {
                    model.updateElement(el.id, { contentPropertyId: '' }, { reason });
                }
            });
        });
    }

    createContentProperty(contentProperty: Omit<ContentProperty, 'uuid'> & { uuid?: string }, reason: Reason = 'user') {
        contentProperty.uuid = contentProperty.uuid || uuidv4();
        this.contentProperties.push(contentProperty as ContentProperty);
        this.eventEmitter.emit('contentPropertyChanged', {
            action: 'create',
            oldValue: null,
            newValue: contentProperty,
            reason,
        });

        return contentProperty.uuid;
    }

    getCopy() {
        const copy = new ContentPropertiesManager();
        copy.contentProperties = deepClone(this.contentProperties);

        return copy;
    }

    toObject() {
        return this.contentProperties.map(exportContentProperty).filter(truthy);
    }
}
